import { FilterParams } from "src/library/utils";
import useAxios from "src/core/shared/hooks/useAxios";

import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "./config.api";

export type ListRolesParams = {filters: FilterParams<Pick<IRole, "code" | "type">>};

type RolesEndpointConfig = {
  LIST_ROLES: EndpointConfig<
    ListRolesParams,
    IRole[]
  >;
  GET_MASQUERADE_ROLE: EndpointConfig<
    void,
    IRole
  >;
};

const BASE_URL = `${process.env.REACT_APP_API_URL}/core/roles`;

const requests: RequestConfig<RolesEndpointConfig> = {
  LIST_ROLES: params => ({
    url: `${BASE_URL}`,
    method: "GET",
    params
  }),
  GET_MASQUERADE_ROLE: () => ({
    url: `${BASE_URL}/masquerade/mode`,
    method: "GET"
  })
};

export type RoleType = "admin" | "customer";
export interface IRole {
  id: number;
  code: string;
  type: RoleType;
  name: "account-manager" | "super-admin" | "billing-customer" | "van-sales-admin" | string;
  permissions: IPermission[];
}

export interface IPermission {
  id: number;
  code: PermissionCodeType;
  name: string;
  relation: PermissionRelation;
  type: PermissionType;
}

export type PermissionType = "create" | "amend" | "delete" | "view";
export type PermissionRelation =
  | "Orders"
  | "Lists"
  | "Products"
  | "Product Categories"
  | "Customers"
  | "Branches"
  | "Users"
  | "Price Codes"
  | "Notifications"
  | "Errors"
  | "Global Settings"
  | "Roles and Permissions"
  | "Process Jobs"
  | "Process Jobs Schedule"
  | "Payment Methods"
  | "Transactions"
  | "Substituion"
  | "Promotion"
  | "Van Sales Ordering";

export type PermissionCodeType =
  | "create-customer"
  | "amend-customer"
  | "delete-customer"
  | "view-customer"
  | "create-branch"
  | "amend-branch"
  | "delete-branch"
  | "view-branch"
  | "create-order"
  | "amend-order"
  | "delete-order"
  | "view-order"
  | "create-list"
  | "amend-list"
  | "delete-list"
  | "view-list"
  | "create-notification"
  | "amend-notification"
  | "delete-notification"
  | "view-notification"
  | "create-product"
  | "amend-product"
  | "delete-product"
  | "view-product"
  | "create-user"
  | "amend-user"
  | "delete-user"
  | "view-user"
  | "create-price-code"
  | "amend-price-code"
  | "delete-price-code"
  | "view-price-code"
  | "create-error"
  | "amend-error"
  | "delete-error"
  | "view-error"
  | "create-global-settings"
  | "amend-global-settings"
  | "delete-global-settings"
  | "view-global-settings"
  | "create-role-permissions"
  | "amend-role-permissions"
  | "delete-role-permissions"
  | "view-role-permissions"
  | "create-product-categories"
  | "amend-product-categories"
  | "delete-product-categories"
  | "view-product-categories"
  | "create-process-jobs"
  | "view-process-jobs"
  | "create-payment-method"
  | "view-payment-method"
  | "amend-payment-method"
  | "delete-payment-method"
  | "create-transaction"
  | "amend-transaction"
  | "view-transaction"
  | "create-substitution"
  | "view-substitution"
  | "amend-substitution"
  | "delete-substitution"
  | "create-promotion"
  | "view-promotion"
  | "amend-promotion"
  | "delete-promotion"
  | "amend-promotion-tags"
  | "view-van-sales-ordering"
  | "view-account-manager-ordering"
  | "create-supplier"
  | "amend-supplier"
  | "delete-supplier"
  | "view-supplier"
  | "view-waste"
  | "create-waste"
  | "amend-waste"
  | "delete-waste"
  | "amend-process-jobs-schedule"
  | "view-process-jobs-schedule";

export const useRolesApi: UseApiRequest<RolesEndpointConfig> = name => useAxios(requests[ name ], name);
